import { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Check, Users, X } from "lucide-react";

import { Button } from "components/ui/Button";
import { CheckboxComponent } from "components/forms/Checkbox";
import { CardsSection } from "components/layout/Section/CardsSection";
import { CORPORATE_ROLE_OPTIONS } from "../form/plateformeRoles";
import { updateCorporateRoles } from "../api/corporateApi";

interface CorporateRolesEditorProps {
  plateformeId: string;
  assignedRoles: string[];
  /** Suppléments attribuables à cette PM (liste blanche ∩ garde de nature). */
  assignableRoles: string[];
  onSaved: () => void;
  onCancel: () => void;
}

const sameSet = (a: string[], b: string[]) =>
  a.length === b.length && a.every((r) => b.includes(r));

/** Rôles de gestion de la corporate (déclinaisons Gestionnaire — annuaires tunnels). */
export const CorporateRolesEditor: React.FC<CorporateRolesEditorProps> = ({
  plateformeId,
  assignedRoles,
  assignableRoles,
  onSaved,
  onCancel,
}) => {
  // Borné aux rôles ATTRIBUABLES : un rôle attribué avant un durcissement de la
  // garde de nature serait invisible (options filtrées) mais renvoyé à chaque
  // sauvegarde — que le serveur rejetterait en bloc. L'écarter d'emblée le fait
  // purger au premier enregistrement.
  const [selected, setSelected] = useState<string[]>(() =>
    assignedRoles.filter((r) => assignableRoles.includes(r)),
  );
  const [isSaving, setIsSaving] = useState(false);

  const dirty = useMemo(() => !sameSet(selected, assignedRoles), [selected, assignedRoles]);

  const toggle = (role: string) => {
    setSelected((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role],
    );
  };

  const save = async () => {
    setIsSaving(true);
    try {
      const response = await updateCorporateRoles(plateformeId, selected);
      if (!response.isSuccess) {
        if (response.errorMessage) toast.error(response.errorMessage);
        return;
      }
      toast.success('Rôles enregistrés');
      onSaved();
    } catch {
      toast.error('Erreur lors de la mise à jour des rôles');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <CardsSection
        icon={<Users className="w-5 h-5 text-primary" />}
        title="Rôles de gestion"
      >
        <p className="-mt-2 text-sm text-gray-500">Capacités de la société dans la configuration des tunnels.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Seules les options attribuables à la nature de la PM sont proposées
            (ex. Consultation porteur de risque : compagnies uniquement). */}
        {CORPORATE_ROLE_OPTIONS.filter((o) => assignableRoles.includes(o.value)).map((option) => (
          <div
            key={option.value}
            className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm hover:border-primary/40 transition-colors"
          >
            <CheckboxComponent
              name={`corporate-role-${option.value}`}
              label={option.label}
              checked={selected.includes(option.value)}
              onChange={() => toggle(option.value)}
            />
            <p className="mt-1 pl-6 text-xs text-gray-500 leading-relaxed">{option.description}</p>
          </div>
        ))}
      </div>

      <div className="mt-4 flex items-center justify-between gap-4">
        <p className="text-xs text-gray-500">
          Ces rôles rendent la société sélectionnable comme gestionnaire dans la configuration
          des tunnels. Les capacités métier du compte sont portées par ses briques.
        </p>
        <div className="flex items-center gap-3">
          <Button outline onClick={onCancel} disabled={isSaving}>
            <X data-slot="icon" />
            Annuler
          </Button>
          <Button color="primary" onClick={save} disabled={!dirty || isSaving}>
            <Check data-slot="icon" />
            Enregistrer
          </Button>
        </div>
      </div>
    </CardsSection>
  );
};
